// ThemeColorButton.jsx

import React, { useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './ThemeColorButton.css';

// Components
import Popup from 'components/popup/Popup';

// Utilities
import { THEME_PALETTE_TOKENS } from 'context/theme/themeTokenSchema';

/**
 * ThemeColorButton
 *
 * Picks a named theme palette color (Red through Purple). The selected value is a
 * theme token id, so each theme mode resolves its own shade when rendered.
 *
 * @param {string} tokenId - The currently selected palette token id, or an empty string.
 * @param {function} onChange - Called with the next token id, or '' when cleared.
 * @param {boolean} disabled - Whether the picker should be disabled.
 * @param {number} popupZIndex - The z-index applied to the palette popup.
 *
 * @returns {ReactNode} The rendered ThemeColorButton.
 */
const ThemeColorButton = React.memo(({
    tokenId = '',
    onChange,
    disabled = false,
    popupZIndex = 2000,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    const selectedColor = tokenId ? theme[tokenId] : '';

    // ----- STATE ----- //

    const [open, setOpen] = useState(false);

    // ----- REFS ----- //

    const anchorRef = useRef(null);

    // ----- HANDLERS ----- //

    /**
     * handleSelect
     *
     * Handles a selection change.
     *
     * @param {string} nextTokenId - The next palette token id, or '' to clear.
     */
    const handleSelect = (nextTokenId) => {
        if (disabled) return;
        onChange?.(nextTokenId);
        setOpen(false);
    };

    // ----- RENDER ----- //

    return (
        <>
            <div
              className={`theme-color-button-container ${disabled ? 'disabled' : ''}`}
              ref={anchorRef}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-label="Select theme color"
              onClick={() => {
                    if (!disabled) setOpen(true);
                }}
              onKeyDown={(event) => {
                    if (disabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpen(true);
                    }
                }}
            >

                {/* SWATCH */}
                <div
                  className={`theme-color-button-swatch ${selectedColor ? '' : 'empty'}`}
                  style={{
                    backgroundColor: selectedColor || 'transparent',
                  }}
                />
            </div>

            <Popup anchorRef={anchorRef} isOpen={open} setOpen={setOpen} zIndex={popupZIndex}>

                {/* POPUP */}
                <div
                  className="theme-color-button-popup"
                >

                    {/* OPTION THEME DEFAULT */}
                    <button
                      className={`theme-color-button-option ${tokenId ? '' : 'selected'}`}
                      type="button"
                      onClick={() => handleSelect('')}
                    >

                        {/* OPTION SWATCH */}
                        <span className="theme-color-button-option-swatch empty" />

                        {/* OPTION LABEL */}
                        <span className="theme-color-button-option-label">Theme default</span>
                    </button>

                    {THEME_PALETTE_TOKENS.map((token) => (

                        /* OPTION */
                        <button
                          className={`theme-color-button-option ${token.id === tokenId ? 'selected' : ''}`}
                          key={token.id}
                          type="button"
                          onClick={() => handleSelect(token.id)}
                        >

                            {/* OPTION SWATCH */}
                            <span
                              className="theme-color-button-option-swatch"
                              style={{
                                backgroundColor: theme[token.id] || 'transparent',
                              }}
                            />

                            {/* OPTION LABEL */}
                            <span className="theme-color-button-option-label">{token.title}</span>
                        </button>
                    ))}
                </div>
            </Popup>
        </>
    );

});

// Dev Tools
ThemeColorButton.displayName = 'ThemeColorButton';

export default ThemeColorButton;
